import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Typography,
} from "@mui/material";
import { ReactElement, useState } from "react";

import { LookupTcrBrandByEinQuery } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { ExternalLink } from "~frontend/components/Link";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import { plural } from "~frontend/util/plural";

import { useTenDlcContext } from "./TenDlcProvider";

export const ImportBrandFromTcrModal = ({
  tcrBrands,
  isOpen,
  onClose,
}: {
  brandRegistrationId: string;
  tcrBrands: LookupTcrBrandByEinQuery["lookupTcrBrandByEin"];
  isOpen: boolean;
  onClose: () => void;
}): ReactElement => {
  const { handleImportExistingBrand } = useTenDlcContext()!;
  const [isImporting, setIsImporting] = useState(false);

  const handleImport = async (brandId: string) => {
    setIsImporting(true);
    await handleImportExistingBrand(brandId);
    setIsImporting(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ overflowY: "scroll" }}>
        <Typography variant="h1Bold" sx={{ mb: 2 }} component="div">
          TCR found {plural(tcrBrands.length, "brand", "brands")} associated
          with this EIN.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box>
          {tcrBrands.map(brand => (
            <FlexBox direction="column" key={brand.brandId} sx={{ mb: 2 }}>
              <Typography variant="h2Bold" component="div">
                {brand.brandId}
              </Typography>

              <Box sx={{ pl: 3, pt: 1 }}>
                <TwoColumnDisplay
                  rows={[
                    {
                      key: "brandId",
                      label: "TCR Brand ID",
                      content: brand.brandId,
                    },
                    {
                      key: "entityType",
                      label: "Entity Type",
                      content: brand.entityType,
                    },
                    {
                      key: "displayName",
                      label: "DBA/Display name",
                      content: brand.displayName,
                    },
                    {
                      key: "companyName",
                      label: "Company name",
                      content: brand.companyName,
                    },
                    {
                      key: "ein",
                      label: "EIN",
                      content: brand.ein,
                    },
                    {
                      key: "address",
                      label: "Address",
                      content: `${brand.street}, ${brand.city}, ${brand.state} ${brand.postalCode}`,
                    },
                    {
                      key: "email",
                      label: "Email",
                      content: brand.email,
                    },
                    {
                      key: "website",
                      label: "Website",
                      content: brand.website,
                    },
                    {
                      key: "phone",
                      label: "Phone",
                      content: brand.phone,
                    },
                    {
                      key: "brandRelationship",
                      label: "Brand Relationship",
                      content: brand.brandRelationship,
                    },
                    {
                      key: "vertical",
                      label: "Vertical",
                      content: brand.vertical,
                    },
                    {
                      key: "identityStatus",
                      label: "Identity Status",
                      content: brand.identityStatus,
                    },
                    {
                      key: "section527",
                      label: "Section 527?",
                      content: brand.optionalAttributes?.section527
                        ? "Yes"
                        : "No",
                    },
                    {
                      key: "taxExemptStatus",
                      label: "Tax Exempt Status",
                      content:
                        brand.optionalAttributes?.taxExemptStatus ?? "N/A",
                    },
                  ]}
                />
              </Box>

              <FlexBox
                justifyContent="flex-end"
                alignItems="center"
                gap={2}
                sx={{ my: 2 }}
              >
                <Box>
                  <ExternalLink
                    href={`https://csp${import.meta.env.STW_ENV_NAME === "production" ? "" : "-staging"}.campaignregistry.com/brands/${brand.brandId}`}
                  >
                    View in TCR
                  </ExternalLink>
                </Box>
                <Button
                  variant="outlined"
                  sx={{ alignItems: "center" }}
                  loading={isImporting}
                  onClick={async () => await handleImport(brand.brandId)}
                >
                  Import {brand.brandId} to this registration
                </Button>
              </FlexBox>
              <Divider sx={{ mb: 2 }} />
            </FlexBox>
          ))}
        </Box>

        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
      </DialogContent>
    </Dialog>
  );
};
